import React, { useContext, useEffect, useState, useMemo } from "react";
import Panel from "./Panel";
import {
  Dimmer,
  Input,
  Loader,
  Message,
  Segment,
  Table,
  Pagination,
  Icon,
  Dropdown,
} from "semantic-ui-react";
import { searchServices } from "../libs/requests/serviceSearch";
import { Link } from "react-router-dom";
import { authContext } from "../containers/AuthProvider";
import { getPartners } from "../libs/requests/getPartnerDetails";
import updatePartnerAsset from "../libs/requests/updatePartnerAsset";
import { Alert } from "react-bootstrap";
import { useLoading, useServices } from "./customHooks";
import { getSupplier } from "../libs/sessionVariable";
import { set } from "lodash";
import { assignService } from "./AssignUtilities";

export default function SearchServices() {
  const [isLoading, setIsLoading] = useLoading(true);
  const [isError, setIsError] = useState(false);
  const [services, setServices] = useServices([]);
  const [info, setInfo] = useState({});
  const { permissions } = useContext(authContext);
  const [isHightide, setIsHighttide] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reloadPage, setReloadPage] = useState(false);

  const [filterByPartner, setFilterByPartner] = useState(
    permissions?.admin ? "" : permissions?.customer || ""
  );
  const [requestedPage, setRequestedPage] = useState(0); 
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setRequestedPage(0);
    }, 500);
  
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const highTidePartners = [
    { Tenant: "Faelix", Partner: "OneHelix", Realms: [""] },
    { Tenant: "Faelix", Partner: "E-volve Solutions", Realms: [""] },
    { Tenant: "Faelix", Partner: "IT4 Automation", Realms: [""] },
    { Tenant: "Faelix", Partner: "Community Waves", Realms: [""] },
    { Tenant: "Faelix", Partner: "Hightide", Realms: [""] },
    { Tenant: "Faelix", Partner: "Netix", Realms: [""] },
    { Tenant: "Faelix", Partner: "Countryside Connections", Realms: [""] }
  ];

  const partnerDropdownOptions = useMemo(() => {
    const sortedOptions = partners
      .map((p) => p.Partner)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({
        key: value,
        text: value,
        value: value,
      }));
  
    sortedOptions.unshift({
      key: "all",
      text: "All",
      value: "",
    });
  
    return sortedOptions;
  }, [partners]);

  useEffect(() => {
    setRequestedPage(0);
  }, [filterByPartner]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const result = await searchServices(
          debouncedSearchTerm,
          requestedPage, 
          10,
          filterByPartner
        );

        setServices(result?.services || []);
        setTotalPages(result?.totalPages || 0);
        setTotalRecords(result?.totalRecords || 0);

        if (permissions.admin) {
          const supplier = await getSupplier("", true);
          setIsHighttide(supplier === "Faelix");
          setPartners(
            supplier === "Faelix"
              ? highTidePartners
              : (await getPartners()).map((p) => ({
                  Tenant: "",
                  Partner: p,
                  Realms: [""],
                }))
          );
        }

        setIsLoading(false);
      } catch (err) {
        setIsError(true);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [filterByPartner, debouncedSearchTerm, requestedPage, reloadPage]);

  const handleSearchChange = (evt) => {
    setSearchTerm(evt.target.value);
  };

  const handleFilterByPartnerChange = (e, { value }) => {
    setFilterByPartner(value);
  };

  const handlePaginationChange = (e, { activePage }) => {
    setRequestedPage(activePage);
  };

  const assign = async (reference, partner) => {
    try {
      // Show loading state
      setInfo({
        type: "info",
        text: `Assigning ${reference} to ${partner}...`,
      });

      // Prepare the partner name based on supplier
      const formattedPartner = isHightide ? `Hightide|${partner}` : partner;

      // Determine supplier based on reference prefix
      let supplier;
      if (reference.toUpperCase().startsWith("Z")) {
        supplier = "ZEN";
      } else if (reference.toUpperCase().startsWith("ORD")) {
        supplier = "GIGANET";
      } else {
        setInfo({
          type: "warning",
          text: `Invalid reference format for ${reference}.`,
        });
        return;
      }

      // Make the API call
      const result = await updatePartnerAsset({
        reference,
        partner: formattedPartner,
        supplier,
      });

      // Handle the response
      if (result) {
        setInfo({
          type: "success",
          text: `Assigned ${reference} to ${partner}. Refreshing data...`,
        });

        // Reset search and trigger data refresh
        setSearchTerm("");
        setReloadPage((prev) => !prev);
      } else {
        setInfo({
          type: "warning",
          text: `Failed to assign ${reference} to ${partner}`,
        });
      }
    } catch (error) {
      setInfo({
        type: "danger",
        text: `Error assigning ${reference}: ${error.message}`,
      });
    }
  };

  if (
    !isLoading &&
    (!permissions.servicesAccess ||
      !permissions ||
      (permissions && !permissions.hasOwnProperty("admin")))
  ) {
    return (
      <Panel>
        <Message error header="You don't have permission to view this page" />
      </Panel>
    );
  }

  if (isError) {
    return (
      <Panel>
        <Message
          error
          header="There was an error searching for services, please check and try again"
        />
      </Panel>
    );
  }

  return (
    <>
      <Panel header={"Services"}>
        {!!info.text && <Alert bsStyle={info.type}>{info.text}</Alert>}

        <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
          <Input
            data-testid="servicesSearchPanel"
            icon={
              searchTerm
                ? {
                    name: "close",
                    link: true,
                    onClick: () => {
                      setSearchTerm("");
                      setRequestedPage(1);
                    },
                  }
                : "search"
            }
            placeholder="Search Services..."
            loading={isLoading}
            size="large"
            onChange={handleSearchChange}
            style={{ flex: 2 }}
            value={searchTerm}
          />
          {permissions?.admin && (
            <Dropdown
              placeholder="All (Show everything)"
              data-testid="assignedToFilter"
              fluid
              search
              selection
              clearable
              options={partnerDropdownOptions}
              onChange={handleFilterByPartnerChange}
              value={filterByPartner}
              style={{ flex: 1 }}
              loading={isLoading}
            />
          )}
        </div>

        {!isLoading && !services.length && (
          <Message info header="No services found" />
        )}

        <Segment style={{ minHeight: "100px", width: "100%" }}>
          <Dimmer active={isLoading} inverted>
            <Loader inverted>Loading</Loader>
          </Dimmer>
          
          {!isLoading && services.length > 0 && (
            <>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Reference</Table.HeaderCell>
                <Table.HeaderCell>Service Status</Table.HeaderCell>
                <Table.HeaderCell>CLI</Table.HeaderCell>
                <Table.HeaderCell>Post Code</Table.HeaderCell>
                <Table.HeaderCell>Customer Reference</Table.HeaderCell>
                <Table.HeaderCell>Product</Table.HeaderCell>
                <Table.HeaderCell>Network</Table.HeaderCell>
                <Table.HeaderCell>Care Level</Table.HeaderCell>
                <Table.HeaderCell>Traffic Weighting</Table.HeaderCell>
                {permissions.admin && (
                  <Table.HeaderCell>Assigned To</Table.HeaderCell>
                )}
                {permissions.admin && <Table.HeaderCell>Actions</Table.HeaderCell>}
              </Table.Row>
            </Table.Header>
            
            {services.length > 0 && (
              <Table.Body>
                {services.map((service) => (
                  <Table.Row key={`${service.reference}-${service.orderDate}`}>
                    <Table.Cell>
                      <Link
                        to={{
                          pathname: `/Services/${service.reference}`,
                          search: `?orderDate=${encodeURIComponent(
                            service.orderDate
                          )}`,
                        }}
                      >
                        {service.reference}
                      </Link>
                    </Table.Cell>
                    <Table.Cell>{service.serviceStatus}</Table.Cell>
                    <Table.Cell>{service.phoneNumber}</Table.Cell>
                    <Table.Cell>{service.postCode}</Table.Cell>
                    <Table.Cell>{service.customerReference}</Table.Cell>
                    <Table.Cell>{service.productDescription}</Table.Cell>
                    <Table.Cell>{service.network}</Table.Cell>
                    <Table.Cell>{service.careLevel}</Table.Cell>
                    <Table.Cell>{service.trafficWeighting}</Table.Cell>
                    {permissions.admin && (
                      <Table.Cell data-testid="assigned-to-cell">
                        {service.assignedTo}
                      </Table.Cell>
                    )}
                    {permissions.admin && (
                      <Table.Cell>
                        <Dropdown
                          text="Assign"
                          fluid
                          button
                          className="icon"
                          scrolling
                          style={{
                            minWidth: "150px",
                            position: "relative",
                          }}
                        >
                          <Dropdown.Menu
                            style={{
                              maxHeight: "300px",
                              overflowY: "auto",
                              minWidth: "250px",
                              position: "absolute",
                              left: 0,
                              zIndex: 1001,
                              marginTop: "5px",
                            }}
                          >
                            {partners.map((o) => (
                              <Dropdown.Item
                                key={o.Partner}
                                onClick={() => assign(service.reference, o.Partner)}
                                style={{
                                  padding: "10px 15px",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {o.Partner}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            )}
          </Table>

          {services.length > 0 && (
            <Pagination
              activePage={requestedPage}
              onPageChange={handlePaginationChange}
              totalPages={totalPages}
              firstItem={null}
              lastItem={null}
              prevItem={{
                content: <Icon name="angle left" />,
                icon: true,
              }}
              nextItem={{
                content: <Icon name="angle right" />,
                icon: true,
              }}
            />
          )}
           </>)}
        </Segment>
      </Panel>
    </>
  );
}